import { EWatchDogEvent } from '@WatchDog/WatchDog.events';
import {
  DoppioPlayer,
  EEvent,
  EPlaybackState as EDoppioPlaybackState,
  TBufferMetrics,
  TDoppioNetworkError,
  TDoppioPlayerConfig,
  TResolution,
  TResolutionsLimits,
} from '@streaming/doppio-player';
import { bind, EPlaybackState, EPlayerType, type TLogLevel } from '@streaming/mouflon-utils';

import { IStreamConfig } from '@customTypes/common';
import { TDoppioEventType } from '@metrics/IMetrics';
import { AbstractWrapper } from '@players/AbstractWrapper';
import { EPlayersEvents } from '@players/Players.events';
import { TMMPEventBus } from '@utils/EventBus';

export interface IDoppioPlayerStreamConfig {
  hlsStreamUrl: string;
}

interface INetworkStats {
  connectDuration: number;
  domainLookupDuration: number;
  sizeInKb: number;
  ttfb: number;
  downloadDuration: number;
  calculatedSpeed: number;
}

const mapDoppioPlaybackStateToMMPPlaybackState: Record<EDoppioPlaybackState, EPlaybackState> = {
  [EDoppioPlaybackState.IDLE]: EPlaybackState.IDLE,
  [EDoppioPlaybackState.LOADING]: EPlaybackState.LOADING,
  [EDoppioPlaybackState.PAUSED]: EPlaybackState.PAUSED,
  [EDoppioPlaybackState.PLAYING]: EPlaybackState.PLAYING,
  [EDoppioPlaybackState.ERROR]: EPlaybackState.ERROR,
};

export class DoppioWrapper extends AbstractWrapper<
  TDoppioPlayerConfig,
  IDoppioPlayerStreamConfig,
  DoppioPlayer
> {
  private _logLevels: TLogLevel[] = [];

  constructor(eventBusInstance: TMMPEventBus) {
    super(eventBusInstance);
  }

  public setStreamConfig(config: IStreamConfig | undefined): DoppioWrapper {
    if (!config || !config.HLSStreamUrl) {
      this._lastKnownStreamConfig = null;
      this._playerInstance?.setUrl('');

      return this;
    }

    this._lastKnownStreamConfig = { hlsStreamUrl: config.HLSStreamUrl };
    this._playerInstance?.setUrl(config.HLSStreamUrl);

    return this;
  }

  public setAbrResolutions(): DoppioWrapper {
    return this;
  }

  public enableAbr(): DoppioWrapper {
    this._isAbrEnabled = true;
    this._playerInstance?.enableABR();

    return this;
  }

  public setResolutionByName(resolutionName: string): DoppioWrapper {
    this._playerInstance?.setResolutionByName(resolutionName);

    return this;
  }

  public setResolutionByBitrate(bitrate: number): DoppioWrapper {
    this._playerInstance?.setResolutionByBitrate(bitrate);

    return this;
  }

  public setLoggerLevel(levels: TLogLevel[]): void {
    this._playerInstance?.setLoggerLevel(levels);
    this._logLevels = levels;
  }

  public disableAbr(): DoppioWrapper {
    this._isAbrEnabled = false;
    this._playerInstance?.disableABR();

    return this;
  }

  public setResolutionLimits(limits: TResolutionsLimits): DoppioWrapper {
    this._playerInstance?.setMaxABRResolution(limits);

    return this;
  }

  get version(): string | undefined {
    return this._playerInstance?.version;
  }

  protected _attachEventListeners(): void {
    if (!this._playerInstance) {
      return;
    }

    this._playerInstance.addEventListener(EEvent.DEBUGGER_CONNECTED, this._onDebuggerConnected);
    this._playerInstance.addEventListener(
      EEvent.PLAYBACK_STATE_CHANGED,
      this._onPlaybackStateChanged,
    );
    this._playerInstance.addEventListener(EEvent.ERROR, this._onDoppioError);
    this._playerInstance.addEventListener(EEvent.VARIANTS_DETECTED, this._onResolutionsAvailable);
    this._playerInstance.addEventListener(EEvent.METRICS_AVAILABLE, this._onMetricsReported);
    this._playerInstance.addEventListener(EEvent.FRAG_LOADED, this._onFragLoaded);
    this._playerInstance.addEventListener(EEvent.LEVEL_LOADED, this._onPlaylistLoaded);
    this._playerInstance.addEventListener(EEvent.LIFECYCLE_EVENT, this._onLifecycleEventReported);
    this._playerInstance.addEventListener(EEvent.LIFECYCLE_ERROR, this._onLifecycleErrorReported);
    this._playerInstance.addEventListener(EEvent.LEVEL_SWITCHED_AUTO, this._onRenditionSwitchAuto);
    this._playerInstance.addEventListener(EEvent.RESOLUTION_CHANGED, this._onResolutionChanged);
    this._playerInstance.addEventListener(EEvent.RESOLUTION_NOT_FOUND, this._onResolutionNotFound);
    this._playerInstance.addEventListener(
      EEvent.FRAGMENT_FILTERED_OUT,
      this._onFragmentFilteredOut,
    );
  }

  protected _detachEventListeners(): void {
    if (!this._playerInstance) {
      return;
    }

    this._playerInstance.removeEventListener(EEvent.DEBUGGER_CONNECTED, this._onDebuggerConnected);
    this._playerInstance.removeEventListener(
      EEvent.PLAYBACK_STATE_CHANGED,
      this._onPlaybackStateChanged,
    );
    this._playerInstance.removeEventListener(EEvent.ERROR, this._onDoppioError);
    this._playerInstance.removeEventListener(
      EEvent.VARIANTS_DETECTED,
      this._onResolutionsAvailable,
    );
    this._playerInstance.removeEventListener(EEvent.METRICS_AVAILABLE, this._onMetricsReported);
    this._playerInstance.removeEventListener(EEvent.FRAG_LOADED, this._onPlaylistLoaded);
    this._playerInstance.removeEventListener(EEvent.LEVEL_LOADED, this._onFragLoaded);
    this._playerInstance.removeEventListener(
      EEvent.LIFECYCLE_EVENT,
      this._onLifecycleEventReported,
    );
    this._playerInstance.removeEventListener(
      EEvent.LIFECYCLE_ERROR,
      this._onLifecycleErrorReported,
    );
    this._playerInstance.removeEventListener(
      EEvent.RESOLUTION_CHANGED,
      this._onRenditionSwitchAuto,
    );
    this._playerInstance.removeEventListener(EEvent.RESOLUTION_CHANGED, this._onResolutionChanged);
    this._playerInstance.removeEventListener(
      EEvent.RESOLUTION_NOT_FOUND,
      this._onResolutionNotFound,
    );
    this._playerInstance.removeEventListener(
      EEvent.FRAGMENT_FILTERED_OUT,
      this._onFragmentFilteredOut,
    );
  }

  protected async _createPlayerInstance(): Promise<void> {
    this._destroyPlayerInstance();

    this._playerInstance = new DoppioPlayer();

    if (this._lastKnownConfig) {
      this._playerInstance.setConfig(this._lastKnownConfig);
    }

    if (this._lastKnownStreamConfig) {
      this._playerInstance.setUrl(this._lastKnownStreamConfig.hlsStreamUrl);
    }

    if (this._videoElement) {
      this._playerInstance.setVideoElement(this._videoElement);
    }

    if (this._isAbrEnabled) {
      this._playerInstance.enableABR();
    } else {
      this._playerInstance.disableABR();
    }

    this._attachEventListeners();
    this._playerInstance?.setLoggerLevel(this._logLevels);
  }

  @bind
  private _onDebuggerConnected({ detail: { panelUrl } }: CustomEvent<{ panelUrl: string }>): void {
    this._eventBusInstance.publish(EPlayersEvents.DEBUGGER_CONNECTED, { debuggerUrl: panelUrl });
  }

  @bind
  private _onPlaybackStateChanged({
    detail: { prevState, nextState },
  }: CustomEvent<{ prevState: EPlaybackState; nextState: EPlaybackState }>): void {
    this._eventBusInstance.publish(EPlayersEvents.PLAYBACK_STATE_CHANGED, {
      nextState: mapDoppioPlaybackStateToMMPPlaybackState[nextState],
      prevState: mapDoppioPlaybackStateToMMPPlaybackState[prevState],
    });
  }

  @bind
  private _onDoppioError({ detail: { error } }: CustomEvent<{ error: Error }>): void {
    this._eventBusInstance.publish(EPlayersEvents.ERROR, { error, playerType: EPlayerType.HLS });
  }

  @bind
  private _onResolutionsAvailable({ detail }: CustomEvent<TResolution[]>): void {
    this._eventBusInstance.publish(EPlayersEvents.RESOLUTIONS_AVAILABLE, { resolutions: detail });
  }

  @bind
  private _onMetricsReported({ detail }: CustomEvent<TBufferMetrics>): void {
    this._eventBusInstance.publish(EWatchDogEvent.ALIVE_PLAYER, { playerType: EPlayerType.HLS });

    this._metricsCollector?.registerMetrics({ targetBuffer: detail.targetBuffer });
    if (detail.latency) {
      this._eventBusInstance.publish(EPlayersEvents.LATENCY_ESTIMATION_AVAILABLE, {
        medianLatency: detail.latency,
        EWMALatency: detail.latency,
      });
    }
  }

  @bind
  private _onFragLoaded(e: CustomEvent<{ netStats: INetworkStats }>): void {
    this._metricsCollector?.registerMetrics({
      chunkLoadDuration: e.detail.netStats.downloadDuration,
      chunkLoadTTFB: e.detail.netStats.ttfb,
      chunkSize: e.detail.netStats.sizeInKb,
    });
  }

  @bind
  private _onPlaylistLoaded(e: CustomEvent<{ netStats: INetworkStats }>): void {
    this._metricsCollector?.registerMetrics({
      playlistLoadTTFB: e.detail.netStats.ttfb,
      playlistLoadDuration: e.detail.netStats.downloadDuration,
      playlistSize: e.detail.netStats.sizeInKb,
    });
  }

  @bind
  private _onLifecycleEventReported({
    detail: { eventType },
  }: CustomEvent<{ eventType: TDoppioEventType }>): void {
    this._metricsCollector?.registerEvent(eventType, {});
  }

  @bind
  private _onRenditionSwitchAuto({
    detail: { nextResolution, prevResolution },
  }: CustomEvent<{ nextResolution: string; prevResolution: string }>): void {
    this._metricsCollector?.registerEvent('RenditionSwitchedAuto', {
      prevResolution,
      nextResolution,
    });

    this._eventBusInstance.publish(EPlayersEvents.AUTO_RESOLUTION_SWITCHED, {
      name: nextResolution,
    });
  }

  @bind
  private _onResolutionChanged({ detail: { name } }: CustomEvent<TResolution>): void {
    this._eventBusInstance.publish(EPlayersEvents.RESOLUTION_SWITCHED, { name });
  }

  @bind
  private _onLifecycleErrorReported({
    detail: { errorType, nativeError },
  }: CustomEvent<{
    errorType: TDoppioEventType;
    nativeError?: TDoppioNetworkError;
  }>): void {
    this._metricsCollector?.registerEvent(errorType, { errorMessage: nativeError?.message || '' });

    this._eventBusInstance.publish(EPlayersEvents.STREAM_LOAD_ERROR, {
      reason: 'HTTP_ERROR',
      httpStatusCode: nativeError?.statusCode,
    });
  }

  @bind
  private _onResolutionNotFound(): void {
    this._eventBusInstance.publish(EPlayersEvents.RESOLUTION_NOT_FOUND, {});
  }

  @bind
  private _onFragmentFilteredOut({
    detail: { reason, fragment },
  }: CustomEvent<{ reason: string; fragment: string }>): void {
    this._metricsCollector?.registerEvent('FragmentFilteredOut', {
      reason,
      fragmentName: fragment,
    });
  }
}
